
import type { QueriesByCategoryTotalsData } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals-data'
import type { QueriesByCategoryData } from '@empathybroker/tsclient-stats/dist/models/queries-by-category/queries-by-category-data'
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type {
  Params,
  TreeChartConfig,
  TreeChartData,
  TreeChartDataItem,
  TreeChartDataItemNode,
} from '@empathyco/insights'
import type { CategoriesDataItem, CategoriesQueryData } from '../../models'
import { FormatEnum } from '@empathybroker/tsclient-stats/dist/enums/format.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { TreeChartComponent } from '@empathyco/insights'

import { defineComponent } from 'vue'

import CategoriesQueriesAside from '../../components/insights/categories/categories-queries-aside.component.vue'
import InsightsNoResults from '../../components/insights/insights-no-results.component.vue'
import { EpbText } from '../../components/shared/epb-text'

import { useAppLoading } from '../../composables/use-app-loading'

import { KpiEnum } from '../../enums'

import { InsightsMixin } from '../../mixins'

import { DownloadUtils } from '../../utils/download.utils'

interface Data {
  categoryChildren: {
    [parentKey: string]: QueriesByCategoryTotalsData[]
  }
  categoryQueries: {
    [parentKey: string]: CategoriesQueryData[]
  }
}

export default defineComponent({
  name: 'Categories',
  components: {
    EpbText,
    CategoriesQueriesAside,
    InsightsNoResults,
  },
  mixins: [InsightsMixin],
  setup() {
    const { loading } = useAppLoading()
    return { loading }
  },
  data() {
    return {
      baseClass: 'epb-categories',
      visitedCategoriesIds: [],
      rootNodeId: 'root',
      component: undefined,
      loadingAside: {
        table: false,
        downloadButton: false,
      },
      componentTimeout: undefined,
      messages: this.$epb.messages.insights.categories,
    }
  },
  computed: {
    period(): { startDate: Date; endDate: Date } {
      const today = new Date()
      const endDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - today.getDay(),
      )
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 7)
      return { startDate, endDate }
    },
    periodString(): string {
      const { startDate, endDate } = this.period
      const dateFormatOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: '2-digit',
        year: '2-digit',
      }
      const startDateString = startDate.toLocaleDateString(this.$epb.locale, dateFormatOptions)
      const endDateString = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate() - 1,
      ).toLocaleDateString(this.$epb.locale, dateFormatOptions)
      return `${startDateString} - ${endDateString}`
    },
    categories(): CategoriesDataItem[] {
      const rootCategory: CategoriesDataItem = {
        id: this.rootNodeId,
        name: this.messages.rootTitle,
        level: 0,
        queryCount: 0,
        add2cartCountPct: 0,
        findabilityPct: 0,
        children: [],
      }
      if (this.data) {
        const firstLevelCategories =
          this.data.categoryChildren[this.getParentKeyFromCategory(rootCategory)]
        rootCategory.queryCount = firstLevelCategories.reduce(
          (queryCount: number, childCategory: QueriesByCategoryTotalsData) => {
            return queryCount + childCategory.queryCount
          },
          0,
        )
        rootCategory.children = this.getCategoryChildren(rootCategory)
        rootCategory.add2cartCountPct =
          rootCategory.children.reduce(
            (add2cartCountPct: number, childCategory: CategoriesDataItem) => {
              return add2cartCountPct + childCategory.add2cartCountPct
            },
            0,
          ) / rootCategory.children.length
        rootCategory.findabilityPct =
          rootCategory.children.reduce(
            (findabilityPct: number, childCategory: CategoriesDataItem) => {
              return findabilityPct + childCategory.findabilityPct
            },
            0,
          ) / rootCategory.children.length
      }
      return [rootCategory]
    },
    componentData(): TreeChartData {
      return this.categories.map(this.getComponentDataItemFromCategory)
    },
    componentParams(): Params<TreeChartConfig, TreeChartData> {
      return {
        config: {
          rootNodeRadius: 100,
          childNodeRadius: 55,
          valueSuffix:
            this.$epb.messages.insights.analytics.kpis[KpiEnum.QUERIES].valueLabel.toUpperCase(),
          selectedDataItemId: this.visitedCategoriesIds[this.visitedCategoriesIds.length - 1],
        },
        data: this.componentData,
      }
    },
    asideVisible(): boolean {
      return !!this.visitedCategoriesIds.length && !!this.categories[0].children.length
    },
    asideData(): CategoriesDataItem {
      const asideCategory = this.getLastVisitedCategory(this.categories[0])
      return {
        ...asideCategory,
        id: asideCategory.level ? asideCategory.id : undefined,
        queries: asideCategory.level
          ? this.data.categoryQueries[this.getParentKeyFromCategory(asideCategory)] || []
          : undefined,
      }
    },
    hasNoResults(): boolean {
      return !this.loading && this.componentData[0].value === 0
    },
  },
  watch: {
    componentParams: {
      handler(params: Params<TreeChartConfig, TreeChartData>): void {
        this.component.update(params)
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new TreeChartComponent()
    this.component.onDataItemNodeClick = (dataItem: TreeChartDataItemNode) =>
      this.onDataItemNodeClick(dataItem)
  },
  mounted(): void {
    this.component.init(this.$refs.component, this.componentParams)
  },
  beforeDestroy(): void {
    this.component.destroy()
  },
  methods: {
    async fetchData(): Promise<Data> {
      const rootCategory = this.categories[0]
      return {
        categoryChildren: {
          [this.getParentKeyFromCategory(rootCategory)]:
            await this.fetchCategoryChildren(rootCategory),
        },
        categoryQueries: {},
      }
    },
    getParentKeyFromCategory(category: CategoriesDataItem): string {
      return `${category.level}${category.parentId}-${category.id}`
    },
    async fetchCategoryChildren(
      category: CategoriesDataItem,
    ): Promise<QueriesByCategoryTotalsData[]> {
      const params = {
        ...this.period,
        rows: 0,
        level: category.level + 1,
        ...(category.id !== this.rootNodeId && { parentId: category.id }),
      }
      const queriesByCategoryTotalsResponse: StatsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.QUERIES_BY_CATEGORY_TOTALS,
        params,
      })
      return queriesByCategoryTotalsResponse.data
    },
    getCategoryChildren(parentCategory: CategoriesDataItem): CategoriesDataItem[] {
      const children =
        this.data.categoryChildren[this.getParentKeyFromCategory(parentCategory)] || []
      return children
        .map((child: QueriesByCategoryTotalsData) => {
          const resultChild: CategoriesDataItem = {
            id: child.categoryId,
            level: parentCategory.level + 1,
            parentId: parentCategory.id,
            name: child.categoryName,
            queryCount: child.queryCount,
            queryCountPct: (child.queryCount / parentCategory.queryCount) * 100,
            findabilityPct: child.successCountPct,
            add2cartCountPct: child.add2cartCountPct,
            children: [],
          }
          resultChild.children = this.getCategoryChildren(resultChild)
          return resultChild
        })
        .sort((category1: CategoriesDataItem, category2: CategoriesDataItem) =>
          category1.name.localeCompare(category2.name),
        )
    },
    getComponentDataItemFromCategory(category: CategoriesDataItem): TreeChartDataItem {
      const resultDataItem: TreeChartDataItem = {
        id: category.id,
        name: category.name.toUpperCase(),
        value: category.queryCount,
        children: [],
      }
      if (!this.visitedCategoriesIds.length) {
        clearTimeout(this.componentTimeout)
        this.componentTimeout = setTimeout(
          () => this.visitedCategoriesIds.push(this.rootNodeId),
          500,
        )
      }
      if (this.visitedCategoriesIds.includes(category.id)) {
        resultDataItem.children = category.children.map((childCategory: CategoriesDataItem) => {
          return this.getComponentDataItemFromCategory(childCategory)
        })
      }
      return resultDataItem
    },
    getLastVisitedCategory(category: CategoriesDataItem = this.categories[0]): CategoriesDataItem {
      const lastVisitedCategoryId = this.visitedCategoriesIds[this.visitedCategoriesIds.length - 1]
      const lastVisitedCategoryParentId =
        this.visitedCategoriesIds[this.visitedCategoriesIds.length - 2]

      if (
        category.id === lastVisitedCategoryId &&
        category.parentId === lastVisitedCategoryParentId &&
        category.level === this.visitedCategoriesIds.length - 1
      ) {
        return category
      }

      return category.children.reduce(
        (found, childCategory) => found || this.getLastVisitedCategory(childCategory),
        undefined,
      )
    },
    onDataItemNodeClick(dataItem: TreeChartDataItemNode): void {
      if (dataItem.level >= this.visitedCategoriesIds.length) {
        this.visitedCategoriesIds.push(dataItem.id)
      } else {
        const wasCategoryVisited = this.visitedCategoriesIds.includes(dataItem.id)
        this.visitedCategoriesIds.splice(dataItem.level, this.visitedCategoriesIds.length)
        if (!wasCategoryVisited || dataItem.id === this.rootNodeId) {
          this.visitedCategoriesIds.push(dataItem.id)
        }
      }
      const lastVisitedCategory = this.getLastVisitedCategory()
      if (
        lastVisitedCategory.id !== this.rootNodeId &&
        !this.data.categoryQueries[this.getParentKeyFromCategory(lastVisitedCategory)]
      ) {
        this.updateCategoryData(lastVisitedCategory)
      }
    },
    async updateCategoryData(category: CategoriesDataItem): Promise<void> {
      this.loadingAside.table = true
      const queriesByCategoryTotalsData = await this.fetchCategoryChildren(category)
      const queriesByCategoryData = await this.fetchQueriesByCategoryData(category)
      const resultCategoryQueries = queriesByCategoryData.map(
        (queriesByCategoryDataItem: QueriesByCategoryData) => ({
          query: queriesByCategoryDataItem.query,
          queryCount: queriesByCategoryDataItem.queryCount,
          queryCountPct: (queriesByCategoryDataItem.queryCount / category.queryCount) * 100,
          findabilityPct: queriesByCategoryDataItem.successCountPct,
          add2cartCountPct: queriesByCategoryDataItem.add2cartCountPct,
        }),
      )
      this.$set(
        this.data.categoryChildren,
        this.getParentKeyFromCategory(category),
        queriesByCategoryTotalsData,
      )
      this.$set(
        this.data.categoryQueries,
        this.getParentKeyFromCategory(category),
        resultCategoryQueries,
      )
      this.loadingAside.table = false
    },
    async fetchQueriesByCategoryData(
      category: CategoriesDataItem,
      download = false,
    ): Promise<QueriesByCategoryData[]> {
      const queriesByCategoryResponse: StatsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.QUERIES_BY_CATEGORY,
        params: {
          ...this.period,
          categoryId: category.id,
          level: category.level,
          ...(category.level > 1 && { parentId: category.parentId }),
          format: download ? FormatEnum.CSV : FormatEnum.JSON,
        },
      })
      return queriesByCategoryResponse.data
    },
    async downloadAsideData(): Promise<void> {
      this.loadingAside.downloadButton = true
      try {
        const lastVisitedCategory = this.getLastVisitedCategory()
        const data = await this.fetchQueriesByCategoryData(lastVisitedCategory, true)
        const fileName = this.getDownloadFileName()
        DownloadUtils.downloadCSV(data, fileName)
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loadingAside.downloadButton = false
      }
    },
    getDownloadFileName(): string {
      const { startDate, endDate } = this.period
      const isSameYear = startDate.getFullYear() === endDate.getFullYear()
      const startDateString = this.getDownloadFileDateString(startDate, !isSameYear)
      const endDateString = this.getDownloadFileDateString(endDate, true)
      const visitedIdsString = this.visitedCategoriesIds.slice(1).join('_')
      return `${startDateString}-${endDateString}_${visitedIdsString}`
    },
    getDownloadFileDateString(date: Date, concatYear: boolean): string {
      const dateMonthString = `0${date.getMonth() + 1}`.slice(-2)
      const dateDayString = `0${date.getDate()}`.slice(-2)
      let dateString = `${dateMonthString}${dateDayString}`
      if (concatYear) {
        dateString = `${dateString}${date.getFullYear().toString().slice(2)}`
      }
      return dateString
    },
  },
})
